import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import IntegrationActions from '../IntegrationActions';
import { getJson, money, shortDate, statusClass } from '../helpers';
import CommitmentSyncModal from './CommitmentSyncModal';
import InvoiceSyncModal from './InvoiceSyncModal';

const SYNC_MODALS = {
  invoice: InvoiceSyncModal,
  commitment: CommitmentSyncModal,
};

export default function ToolListPage({
  title,
  crumb,
  description,
  projectVuid,
  fetcher,
  columns,
  retrieveKind,
  retrieveKinds,
  sendKind,
  sendPayload,
  hideChrome,
  syncKind,
  filters = [],
}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projectName, setProjectName] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncOpen, setSyncOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const loads = [fetcher()];
    if (projectVuid) {
      loads.push(getJson(`/api/projects/${projectVuid}`).catch(() => null));
    }
    Promise.all(loads)
      .then(([data, project]) => {
        if (cancelled) return;
        setRows(Array.isArray(data) ? data : []);
        if (project?.project_name) setProjectName(project.project_name);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || `Unable to load ${title.toLowerCase()}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid, title, refreshKey]);

  const visibleColumns = useMemo(
    () => columns.filter((column) => !column.showWhen || column.showWhen(filterValues)),
    [columns, filterValues]
  );

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesFilters = filters.every((filter) => {
        const selected = filterValues[filter.key] || 'all';
        if (selected === 'all') return true;
        return String(row[filter.key] || '') === selected;
      });
      if (!matchesFilters) return false;
      if (!term) return true;
      return columns.some((column) => String(row[column.key] || '').toLowerCase().includes(term));
    });
  }, [rows, query, columns, filters, filterValues]);
  const hasRowSend = syncKind !== 'invoice' && rows.some((row) => row.sendKind);
  const reload = () => setRefreshKey((value) => value + 1);

  const columnClass = (column) => {
    const classes = [];
    if (column.money) classes.push('pc-right');
    if (column.nowrap || column.money || column.status) classes.push('pc-nowrap');
    return classes.join(' ') || undefined;
  };
  const hasColumnWidths = visibleColumns.some((column) => column.width);

  const renderCell = (row, column) => {
    const value = row[column.key];
    if (column.money) return money(value);
    if (column.date) return shortDate(value);
    if (column.status) {
      return <span className={`pc-pill ${statusClass(value)}`}>{value || '—'}</span>;
    }
    return value === 0 ? '0' : value || '—';
  };

  const SyncModal = SYNC_MODALS[syncKind];
  const actions = SyncModal ? (
    <button type="button" className="pc-btn pc-btn-secondary" onClick={() => setSyncOpen(true)}>
      Sync
    </button>
  ) : (
    <IntegrationActions
      projectVuid={projectVuid}
      retrieveKind={retrieveKind}
      retrieveKinds={retrieveKinds}
      sendKind={sendKind}
      sendPayload={sendPayload}
      onComplete={reload}
    />
  );

  return (
    <div className={hideChrome ? undefined : 'pc-page'}>
      {hideChrome ? (
        <div className="pc-title-row">
          <span />
          {actions}
        </div>
      ) : (
        <>
          <div className="pc-crumb">
            {projectVuid ? (
              <>
                <Link to="/procore">Portfolio</Link>
                {' / '}
                <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
                {' / '}
                {title}
              </>
            ) : (
              crumb || title
            )}
          </div>
          <div className="pc-title-row">
            <h1>{title}</h1>
            {actions}
          </div>
        </>
      )}
      <div className="pc-toolbar">
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Filter ${title.toLowerCase()}`} />
        {filters.map((filter) => (
          <label key={filter.key} className="pc-toolbar-label">
            {filter.label}
            <select
              value={filterValues[filter.key] || 'all'}
              onChange={(event) =>
                setFilterValues((current) => ({ ...current, [filter.key]: event.target.value }))
              }
            >
              {(filter.options || []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
      <div className="pc-card">
        {loading ? (
          <div className="pc-empty">Loading…</div>
        ) : error ? (
          <div className="pc-empty">{error}</div>
        ) : visible.length === 0 ? (
          <div className="pc-empty">No {title.toLowerCase()} to show.</div>
        ) : (
          <div className="pc-table-scroll">
          <table className={hasColumnWidths ? 'pc-table pc-table-fixed' : 'pc-table'}>
            {hasColumnWidths ? (
              <colgroup>
                {visibleColumns.map((column) => (
                  <col key={column.key} style={column.width ? { width: column.width } : undefined} />
                ))}
                {hasRowSend ? <col style={{ width: '120px' }} /> : null}
              </colgroup>
            ) : null}
            <thead>
              <tr>
                {visibleColumns.map((column) => (
                  <th key={column.key} className={columnClass(column)}>
                    {column.label}
                  </th>
                ))}
                {hasRowSend ? <th>Procore</th> : null}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr
                  key={row.id}
                  className={row.href ? 'pc-clickable' : undefined}
                  onClick={() => row.href && navigate(row.href)}
                >
                  {visibleColumns.map((column, index) => (
                    <td key={column.key} className={columnClass(column)}>
                      {index === 0 && row.href ? (
                        <Link to={row.href} onClick={(event) => event.stopPropagation()}>
                          {renderCell(row, column)}
                        </Link>
                      ) : (
                        renderCell(row, column)
                      )}
                    </td>
                  ))}
                  {hasRowSend ? (
                    <td>
                      {row.sendKind ? (
                        <IntegrationActions
                          compact
                          projectVuid={projectVuid}
                          sendKind={row.sendKind}
                          sendPayload={row.sendPayload}
                          onComplete={reload}
                        />
                      ) : (
                        '—'
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      {SyncModal && syncOpen ? (
        <SyncModal
          projectVuid={projectVuid}
          onClose={() => setSyncOpen(false)}
          onComplete={() => {
            setSyncOpen(false);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
